import { useMutation, useQueryClient, type UseMutationResult } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { STAY_PLAN_TABLES } from "@/lib/daycare-hotel/stayPlans";
import type { StayPlanContract } from "@/hooks/useStayPlanContracts";
import { buildStayPlanStatus } from "@/hooks/useStayPlanStatus";

export interface ChargeStayPlanNowInput {
  contract: StayPlanContract;
  today: string;
}

/**
 * Cobrar a mensalidade agora. Devolve o id da venda para a tela navegar até ela.
 *
 * Quando já existe uma cobrança em aberto do ciclo, reusamos a venda dela em vez
 * de criar outra — duas vendas para a mesma mensalidade é o erro que este
 * caminho existe para evitar.
 */
export async function chargeStayPlanNow({ contract, today }: ChargeStayPlanNowInput): Promise<string> {
  const status = buildStayPlanStatus(contract, today);
  if (status.openChargeSaleId) return status.openChargeSaleId;

  // Sem clinic_id a RLS de `sales` recusa o insert com 42501: a policy é
  // user_has_clinic_access(clinic_id), e um null nunca passa nela.
  if (!contract.clinicId) throw new Error("stay_plan_contract_missing_clinic");

  const tables = STAY_PLAN_TABLES[contract.module];
  const { data: auth } = await supabase.auth.getUser();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: sale, error: saleError } = await (supabase as any)
    .from("sales")
    .insert({
      sale_number: `V${Date.now()}`,
      clinic_id: contract.clinicId,
      pet_id: contract.petId,
      owner_id: contract.ownerId,
      status: "ABERTA",
      subtotal: contract.price,
      total: contract.price,
      created_by: auth?.user?.id ?? null,
    })
    .select("id")
    .single();
  if (saleError) throw saleError;

  // A venda já existe daqui para baixo. Se algum passo falhar, ela é desfeita:
  // uma comanda em aberto sem cobrança ligada ficaria órfã na tela do
  // responsável e a próxima tentativa criaria outra.
  const rollback = async () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await (supabase as any).from("sale_items").delete().eq("sale_id", sale.id);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await (supabase as any).from("sales").delete().eq("id", sale.id);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error: itemError } = await (supabase as any).from("sale_items").insert({
    sale_id: sale.id,
    item_name: contract.planName,
    item_id: null,
    quantity: 1,
    unit_price: contract.price,
    line_total: contract.price,
  });
  if (itemError) {
    await rollback();
    throw itemError;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error: chargeError } = await (supabase as any).from(tables.charges).insert({
    contract_id: contract.id,
    sale_id: sale.id,
    // Pacote de diárias não tem ciclo nem vencimento: quem clicou em "cobrar
    // agora" está cobrando hoje. due_on é NOT NULL, então null derrubava o
    // insert com 23502.
    due_on: status.dueOn ?? today,
    period_starts_on: status.cycle?.start ?? contract.startsOn,
    period_ends_on: status.cycle?.end ?? contract.startsOn,
  });
  if (chargeError) {
    await rollback();
    throw chargeError;
  }

  return sale.id as string;
}

export function useChargeStayPlanNow(): UseMutationResult<string, unknown, ChargeStayPlanNowInput> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: chargeStayPlanNow,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["stay-plan-contracts"] });
      queryClient.invalidateQueries({ queryKey: ["stay-plan-rows"] });
    },
  });
}
